// src/pages/Customers.jsx
import React from "react";
import ConfirmModal from "../components/ConfirmModal";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { getCurrentUser } from "../utils/auth";
import { getUserAccessInfo } from "../utils/accessFilters";
import { buildAccessHeaders } from "../utils/accessHeaders";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const API_PATH = "/customers";

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const ensureIds = (arr = []) => arr.map((r) => (r && r._id ? r : { _id: genId(), ...r }));

const COLUMNS = [
  { key: "cust_code", label: "Cust Code*", width: 200, required: true, placeholder: "e.g. CUST-01" },
  { key: "wms_code", label: "WMS Code", width: 200, placeholder: "Alternate code" },
  { key: "wm_supplier_no", label: "WM SupplierNo", width: 200, placeholder: "Walmart supplier #" },
  { key: "cust_name", label: "Customer Name", width: 260, placeholder: "Company name" },
  { key: "poc", label: "POC", width: 180, placeholder: "Contact person" },
  { key: "contact_no", label: "Contact No.", width: 160, placeholder: "+1 000-000-0000" },
  { key: "email", label: "Email", width: 240, placeholder: "name@example.com" },
  { key: "address", label: "Address", width: 420, placeholder: "Street, City, State" },
  { key: "remark", label: "Remark", width: 260, placeholder: "Notes / comment" },
];

export default function Customers() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [isDeleteOpen, setDeleteOpen] = React.useState(false);
  const me = getCurrentUser();
  const meEmail = me?.email || "";
  const meRole = me?.role || "";
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = React.useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const requestHeaders = React.useMemo(
    () => buildAccessHeaders({ email: meEmail, role: meRole }, accessInfo, { includeCust: true, includeWhs: false }),
    [meEmail, meRole, accessInfo]
  );
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "admin-customers");
  const canEdit = permissionLevel === "Full" || permissionLevel === "Limited";

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${API_BASE}${API_PATH}`, { headers: requestHeaders });
        const j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j.error || "load failed");
        setRows(
          ensureIds(
            (j.rows || []).map((row) => ({
              wms_code: "",
              wm_supplier_no: "",
              ...row,
            }))
          )
        );
        setSelectedIds(new Set());
      } catch (e) {
        console.error(e);
        setErr("Load failed.");
      } finally {
        setLoading(false);
      }
    })();
  }, [requestHeaders]);

  const onAddRow = () => {
    if (!canEdit) return;
    setRows((rs) => [
      {
        _id: genId(),
        cust_code: "",
        wms_code: "",
        wm_supplier_no: "",
        cust_name: "",
        poc: "",
        contact_no: "",
        email: "",
        address: "",
        remark: "",
      },
      ...rs,
    ]);
  };

  const toggleSelect = (rowId, checked) => {
    setSelectedIds((prev) => {
      if (!canEdit) return prev;
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const askDelete = () => {
    if (!canEdit || selectedIds.size === 0) return;
    setDeleteOpen(true);
  };
  const confirmDelete = () => {
    if (!canEdit) return;
    setRows((prev) => prev.filter((r) => !selectedIds.has(r._id)));
    setSelectedIds(new Set());
    setDeleteOpen(false);
  };

  const onCellChange = (idx, key, v) => {
    setRows((rs) => {
      if (!canEdit) return rs;
      const copy = [...rs];
      copy[idx] = { ...copy[idx], [key]: v };
      return copy;
    });
  };

  const dupCodeSet = React.useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const code = (r.cust_code || "").trim().toLowerCase();
      if (!code) continue;
      map.set(code, (map.get(code) || 0) + 1);
    }
    return new Set([...map.entries()].filter(([, c]) => c > 1).map(([k]) => k));
  }, [rows]);

  const onSave = async () => {
    if (!canEdit) {
      setErr("You do not have permission to edit Customers.");
      return;
    }
    setErr("");

    for (const r of rows) {
      if ((r.cust_code || "").trim() === "") {
        setErr("Cust Code is required.");
        return;
      }
    }
    if (dupCodeSet.size > 0) {
      setErr("Duplicate Cust Code is not allowed.");
      return;
    }

    setLoading(true);
    try {
      const hdrs = { ...requestHeaders, "Content-Type": "application/json" };
      const r = await fetch(`${API_BASE}${API_PATH}`, {
        method: "PUT",
        headers: hdrs,
        body: JSON.stringify({ rows }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "save failed");
    } catch (e) {
      console.error(e);
      setErr(e.message || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-2 flex items-center gap-2">
        <h1 className="text-xl font-bold">Customers</h1>
        <UserAccessSummary className="ml-2" />
        {!canEdit && (
          <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
            Read only
          </span>
        )}
        <button
          className="px-3 py-1 rounded bg-gray-100 border disabled:opacity-50"
          onClick={onAddRow}
          disabled={!canEdit}
        >
          + Add Row
        </button>
        <button
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
          onClick={onSave}
          disabled={loading || !canEdit}
        >
          {loading ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={askDelete}
          disabled={!canEdit || selectedIds.size === 0}
          className={
            !canEdit || selectedIds.size === 0
              ? "px-3 py-1 rounded bg-gray-200 text-gray-500 cursor-not-allowed"
              : "px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
          }
          title={selectedIds.size ? `Delete ${selectedIds.size} selected row(s)` : "Select rows to enable"}
        >
          Delete
        </button>
        {err && <span className="text-red-600 ml-2">{err}</span>}
      </div>

      <div className="overflow-auto border rounded">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th style={{ width: 36, minWidth: 36 }} className="sticky top-0 bg-white border-b px-2 py-2">
                &nbsp;
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="sticky top-0 bg-white border-b px-2 py-2 text-left text-sm font-semibold"
                  style={{ width: col.width, minWidth: col.width }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="text-center text-gray-500 py-8">
                  No data.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const codeLower = (row.cust_code || "").trim().toLowerCase();
                const isDup = !!codeLower && dupCodeSet.has(codeLower);
                return (
                  <tr key={row._id} className="border-t">
                    <td style={{ width: 36, minWidth: 36 }} className="px-2 py-1 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row._id)}
                        onChange={(e) => toggleSelect(row._id, e.target.checked)}
                        disabled={!canEdit}
                      />
                    </td>

                    {COLUMNS.map((col) => (
                      <td key={col.key} className="px-2 py-1 align-top" style={{ width: col.width }}>
                        <input
                          className={`w-full border rounded px-2 py-1 text-sm ${
                            col.key === "cust_code" && isDup ? "border-red-500" : ""
                          }`}
                          placeholder={col.placeholder || ""}
                          value={row[col.key] ?? ""}
                          onChange={(e) => onCellChange(idx, col.key, e.target.value)}
                          title={col.key === "cust_code" && isDup ? "Duplicate code" : ""}
                          disabled={!canEdit}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={isDeleteOpen}
        title="Remove selected rows"
        message={`You're about to remove ${selectedIds.size} selected ${
          selectedIds.size === 1 ? "row" : "rows"
        }.\n\nNote: They won't be permanently deleted until you click "Save".`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  );
}
